import React, { use, useCallback, useMemo, useRef, useState } from "react";
import ScreenCard, { ButtonType, ScreenCardHandle, ScreenCardOptionalAction } from "../ScreenCard";
import { StackScreenProps } from "@react-navigation/stack";
import { useMutation, useQuery } from "@apollo/client/react";
import {
    GET_MISSION_OVERTIME_STATUS,
    GET_MISSION_VACATION_STATUS,
    GET_PAYROLL_BREAKDOWN,
    GET_PAYROLL_BREAKDOWN_SUMMARY,
    SAVE_PAYROLL_BREAKDOWN,
    SUBMIT_PAYROLL_BREAKDOWN,
} from "../../graphql/timesheet";
import ScreenHeader from "../user/ScreenHeader";
import { useI18n } from "../../hooks/i18n-hooks";
import Loading from "../../components/Loading";
import {
    AbsenceInput,
    AmendmentSimpleSigningStateType,
    AmendmentState,
    AuxiliarySalaryInput,
    BonusInput,
    PayrollBreakdownInput,
    PayrollBreakdownStatus,
    PayrollSourceType,
    ProfileType,
    RepetitionType,
    TimeSheetState,
} from "../../__generated__/graphql";
import PayrollBreakdownEditorHourly from "../../components/editors/payroll-breakdown/PayrollBreakdownEditorHourly";
import PayrollBreakdownEditorMonthly from "../../components/editors/payroll-breakdown/PayrollBreakdownEditorMonthly";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { View } from "react-native";
import { SwipeButton } from "../../components/SwipeButton";
import { PayrollBreakdownData } from "../../components/editors/payroll-breakdown/PayrollBreakdownEditor";
import VStack from "../../ui_atoms/VStack";
import { useApolloErrorSnackbar, useSnackbar } from "../../hooks/snackbar";
import { StackActions } from "@react-navigation/native";
import { GET_MISSION } from "../../graphql/core";
import { ProfileSwitcherManagerContext } from "../../contexts/ProfileSwitcherManager";
import { FULL_DATE_FORMAT, useDateFormatter, useDateRangeFormatter } from "../../hooks/date";
import PayrollBreakdownSummary, {
    attachGrossAmountsToSummaryRows,
    SummaryRowWithGross,
    SummaryRowWithSubValues,
} from "./PayrollBreakdownSummary";
import { useTracking } from "../../hooks/tracking";
import { buildErrorHeadband } from "../ScreenCardHeadband";
import { GET_ACTIVITY_TODO_FEED, GET_ALL_PROFILES_ACTIVITIES_INFO } from "../../graphql/activity";
import { isEqual } from "lodash";
import { useDebouncedCallback } from "use-debounce";
import { useTheme } from "../../hooks/use-theme";
import { useScrollHeaderColorSync } from "../../hooks/use-scroll-header-color-sync";
import { SelectChooser } from "../../components/select/SelectChooser";
import { ExtrasSummaryRowFunctions } from "../../components/editors/payroll-breakdown/PayrollBreakdownExtras";
import { usePayrollBreakdownFormatter } from "../../hooks/payroll-beakdown-utils";
import PayrollBreakdownInfo from "../../components/editors/payroll-breakdown/PayrollBreakdownInfo";
import { isEmptyPayrollBreakdownPeriod } from "../../components/editors/payroll-breakdown/utils";
import Callout from "../../ui_atoms/Callout";
import { useIntercom } from "../../hooks/intercom";
import { useSetAlert } from "../../hooks/use-set-alert";
import { GET_MISSION_AMENDMENTS } from "../../graphql/amendment";

type PayrollBreakdownEditorScreenProps = StackScreenProps<
    RootStackParamList,
    "PayrollBreakdownEditor"
>;

const EDITORS = {
    hourly: PayrollBreakdownEditorHourly,
    daily: PayrollBreakdownEditorHourly,
    monthly: PayrollBreakdownEditorMonthly,
    package: PayrollBreakdownEditorHourly,
    undefined: null,
};

// Signing states of an unsigned amendment that block submitting the payroll breakdown.
const PENDING_AMENDMENT_SIGNING_STATES = [
    AmendmentSimpleSigningStateType.WaitingClient,
    AmendmentSimpleSigningStateType.WaitingWorker,
    AmendmentSimpleSigningStateType.WaitingBasquiat,
];

const sanitizeBonuses = <T extends BonusInput>(
    bonuses: Array<T> | undefined,
): Array<BonusInput> | undefined => {
    if (!bonuses) {
        return undefined;
    }
    return bonuses.map((bonus) => {
        return {
            description: bonus.description,
            amount: bonus.amount,
        };
    });
};

const sanitizeAuxiliarySalaries = <T extends AuxiliarySalaryInput>(
    auxiliarySalaries: Array<T> | undefined,
): Array<AuxiliarySalaryInput> | undefined => {
    if (!auxiliarySalaries) {
        return undefined;
    }
    return auxiliarySalaries.map((auxiliarySalary) => {
        return {
            description: auxiliarySalary.description,
            duration: auxiliarySalary.duration,
            grossAmount: auxiliarySalary.grossAmount,
        };
    });
};

export type CustomRowsType = {
    category: string;
    productKey: string;
    index?: number;
} & ExtrasSummaryRowFunctions;

type UseSummaryRowsType = {
    payrollBreakdownSummaryRows: SummaryRowWithGross[];
    absences: AbsenceInput[];
    overtimePaid100?: number;
    overtimePaid125?: number;
};

/**
 * This hook allows to customize some of the payrollBreakdown summary rows
 * Especially, we use this hook to allow clicking on items directly in the summary payrollbreakdown, allowing to edit/delete items with custom functions
 */
const useSummaryRows = ({
    payrollBreakdownSummaryRows,
    absences,
    overtimePaid100 = 0,
    overtimePaid125 = 0,
}: UseSummaryRowsType) => {
    const dateRangeFormatter = useDateRangeFormatter();
    const i18n = useI18n();

    const overtimeDelta = (overtimePaid100 || overtimePaid125) / 60;

    const rowsWithAbsences = useMemo(
        () => [
            ...payrollBreakdownSummaryRows.map((e) =>
                e.productKey === "salary_overtime_0"
                    ? { ...e, quantity: e.quantity - overtimeDelta }
                    : e,
            ),
            ...(overtimeDelta
                ? [
                      {
                          id: "overtime_delta",
                          category: "salary",
                          productKey: "salary_overtime_0_from_balance",
                          quantity: overtimeDelta,
                          // Synthetic display row: SummaryRow fields with no value here are null.
                          description: null,
                          detailedDescription: null,
                          repetition: null,
                          extra: {
                              percent: overtimePaid100 ? 100 : overtimePaid125 ? 125 : undefined,
                          },
                          unitPrice:
                              payrollBreakdownSummaryRows.find(
                                  (e) => e.productKey === "salary_overtime_0",
                              )?.unitPrice || 0,
                      },
                  ]
                : []),
            ...absences.map((absence) => ({
                id: absence.id,
                category: "absence",
                productKey: absence.type,
                description:
                    absence.start && absence.end
                        ? `${i18n.t(`absence.type.${absence.type}`)} ${dateRangeFormatter(
                              new Date(absence.start),
                              new Date(absence.end),
                              "long",
                              false,
                              false,
                              true,
                          )}${absence.comment ? ` : ${absence.comment}` : ""}`
                        : "",
                quantity: 1,
                unitPrice: 0,
                // Synthetic absence row: SummaryRow fields with no value here are null.
                detailedDescription: null,
                repetition: null,
            })),
        ],
        [
            payrollBreakdownSummaryRows,
            overtimeDelta,
            overtimePaid100,
            overtimePaid125,
            absences,
            i18n,
            dateRangeFormatter,
        ],
    );

    const [customRows, setCustomRows] = useState<CustomRowsType[]>([]);
    const [selectChooserCurrentItem, setSelectChooserCurrentItem] =
        useState<ExtrasSummaryRowFunctions>();

    const finalRows = useMemo(() => {
        const getFilteredArrays = (category: string) =>
            [
                rowsWithAbsences.filter((r) => r.category === category),
                customRows.filter((r) => r.category === category),
            ] as const;

        const allCategories = [...new Set(rowsWithAbsences.map((obj) => obj.category))];

        return allCategories.reduce((result, value) => {
            const [_rows, _customRows] = getFilteredArrays(value);

            const rowsWithFunctions = _rows.map((r, index) => {
                const customRowData = _customRows.find((e) =>
                    e.index !== undefined ? index === e.index : e.productKey === r.productKey,
                );

                if (customRowData) {
                    return {
                        ...r,
                        icon: "write",
                        onPress: () => {
                            setSelectChooserCurrentItem({
                                onEdit: customRowData.onEdit,
                                onDelete: customRowData.onDelete,
                            });
                        },
                    };
                } else {
                    return r;
                }
            });

            return [...result, ...rowsWithFunctions];
        }, [] as SummaryRowWithSubValues[]);
    }, [customRows, rowsWithAbsences]);

    const selectChooser = useMemo(
        () => (
            <SelectChooser
                visible={!!selectChooserCurrentItem}
                onDismiss={() => {
                    setSelectChooserCurrentItem(undefined);
                }}
                dismissOnPress
                selectable={false}
                searchItems={[
                    {
                        label: "",
                        value: "",
                        labels: [{ text: i18n.t("edit"), mode: "cta" }],
                        onPress: selectChooserCurrentItem?.onEdit,
                        rightActionMode: "empty",
                    },
                    {
                        label: "",
                        value: "",
                        labels: [{ text: i18n.t("delete"), mode: "redCta" }],
                        onPress: () => {
                            void selectChooserCurrentItem?.onDelete();
                            setSelectChooserCurrentItem(undefined);
                        },
                        rightActionMode: "empty",
                    },
                ]}
            />
        ),
        [i18n, selectChooserCurrentItem],
    );

    return { summaryRows: finalRows, setCustomSummaryRows: setCustomRows, selectChooser };
};

const PayrollBreakdownEditorScreen = ({ route, navigation }: PayrollBreakdownEditorScreenProps) => {
    const payrollBreakdownId = route.params.payrollBreakdownId;
    const i18n = useI18n();
    const { colors } = useTheme();
    const { trackEvent } = useTracking();
    const screenCardRef = useRef<ScreenCardHandle>(null);

    const { data, error, loading } = useQuery(GET_PAYROLL_BREAKDOWN, {
        variables: { payrollBreakdownId },
        skip: !payrollBreakdownId,
        fetchPolicy: "cache-and-network",
    });
    const payrollBreakdown = data?.getPayrollBreakdown;

    const {
        data: vacationStatusData,
        error: vacationStatusError,
        loading: vacationStatusLoading,
    } = useQuery(GET_MISSION_VACATION_STATUS, {
        variables: { missionId: payrollBreakdown?.missionInfo.id || "" },
        skip: !payrollBreakdown,
        fetchPolicy: "cache-and-network",
    });

    const { data: { getMissionAmendments = [] } = {} } = useQuery(GET_MISSION_AMENDMENTS, {
        fetchPolicy: "cache-and-network",
        variables: {
            missionId: payrollBreakdown?.missionInfo.id || "",
            includeRejected: false,
        },
        skip: !payrollBreakdown?.missionInfo.id,
    });

    const vacationStatus = vacationStatusData?.getMissionVacationStatus;

    // Both queries run cache-and-network: they hand out the row cached by the payroll breakdown
    // list before the server answers, and that cached row can carry outdated timesheet aggregates
    // (delta work, worked days, overtime). The editors snapshot those props into a react-hook-form
    // and into local state on mount and never read them again, so mounting on the cached row froze
    // the form on stale values — the recap showed "8 h 24 à compenser" while everything derived
    // from the form still computed with 0, hiding the "pay overtime" entry (BAS-2891).
    // Latched: once the editor is mounted a later background refetch must never unmount it and
    // throw away what the user is filling in.
    const isEditorDataSettled =
        !!payrollBreakdown && !!vacationStatus && !loading && !vacationStatusLoading;
    const [canRenderEditor, setCanRenderEditor] = useState(false);
    if (isEditorDataSettled && !canRenderEditor) {
        setCanRenderEditor(true);
    }

    const { data: missionData } = useQuery(GET_MISSION, {
        variables: { id: payrollBreakdown?.missionInfo.id || "" },
        skip: !payrollBreakdown?.missionInfo.id,
        fetchPolicy: "cache-and-network",
    });
    const [submitPayrollBreakdown, { loading: processing }] = useMutation(
        SUBMIT_PAYROLL_BREAKDOWN,
        {
            // The mission screen stays mounted behind the editor, so its balances only
            // refresh if this submit invalidates them (BAS-2943).
            refetchQueries: [
                GET_ACTIVITY_TODO_FEED,
                GET_ALL_PROFILES_ACTIVITIES_INFO,
                GET_MISSION_OVERTIME_STATUS,
                GET_MISSION_VACATION_STATUS,
            ],
        },
    );
    const [savePayrollBreakdown, { loading: saving, error: savingError }] =
        useMutation(SAVE_PAYROLL_BREAKDOWN);

    useApolloErrorSnackbar(
        savingError,
        undefined,
        i18n.t("screens.payroll_breakdown_editor.error_saving"),
    );

    const onBack = useCallback(() => {
        navigation.goBack();
    }, [navigation]);
    const { profileType } = use(ProfileSwitcherManagerContext);
    const formatDate = useDateFormatter();
    const Editor = EDITORS[payrollBreakdown?.missionInfo?.earningType || "undefined"];
    const [validData, setValidData] = React.useState<PayrollBreakdownData | null>(null);
    const [_, setData] = React.useState<PayrollBreakdownData>({});

    const isFromTimesheet = useMemo(() => {
        return payrollBreakdown?.missionInfo.payrollSource === PayrollSourceType.Lift;
    }, [payrollBreakdown]);

    const _buildPayrollBreakdownInputData = useCallback(
        (data: PayrollBreakdownData): PayrollBreakdownInput => {
            return !isFromTimesheet
                ? {
                      deltaWork: data.deltaWork,
                      usedVacationAmount: data.usedVacationAmount,
                      usedVacationMinutes: data.usedVacationMinutes,
                      totalWorkedDays: data.totalWorkedDays?.toFixed(), // decimal are string for graphql and workedDays are in fact just integer
                      totalWork100: data.totalWork100,
                      totalWork115: data.totalWork115,
                      totalWork125: data.totalWork125,
                      totalWork150: data.totalWork150,
                      totalWork200: data.totalWork200,
                      absences: data.absences,
                      bonuses: sanitizeBonuses(data.bonuses),
                      auxiliarySalaries: sanitizeAuxiliarySalaries(data.auxiliarySalaries),
                      effectiveFees: data.effectiveFees,
                      dynamicFees: data.dynamicFees,
                      overtimePaid100: data.overtimePaid100,
                      overtimePaid125: data.overtimePaid125,
                  }
                : {
                      bonuses: sanitizeBonuses(data.bonuses),
                      auxiliarySalaries: sanitizeAuxiliarySalaries(data.auxiliarySalaries),
                      effectiveFees: data.effectiveFees,
                      dynamicFees: data.dynamicFees,
                      overtimePaid100: data.overtimePaid100,
                      overtimePaid125: data.overtimePaid125,
                  };
        },
        [isFromTimesheet],
    );

    const {
        data: payrollBreakdownData,
        error: payrollBreakdownError,
        loading: payrollBreakdownLoading,
    } = useQuery(GET_PAYROLL_BREAKDOWN_SUMMARY, {
        variables: {
            payrollBreakdownId,
            payrollBreakdownInput: validData ? _buildPayrollBreakdownInputData(validData) : {},
        },
        skip: !validData,
        fetchPolicy: "cache-and-network",
    });

    // The server rows only carry the invoiced amount; the labels also show the gross,
    // taken from the form data the displayed summary was computed from.
    const summaryRowsWithGross = useMemo(
        () =>
            attachGrossAmountsToSummaryRows(
                payrollBreakdownData?.getPayrollBreakdownSummary.rows || [],
                {
                    bonuses: validData?.bonuses,
                    auxiliarySalaries: validData?.auxiliarySalaries,
                },
            ),
        [
            payrollBreakdownData?.getPayrollBreakdownSummary.rows,
            validData?.bonuses,
            validData?.auxiliarySalaries,
        ],
    );

    const { summaryRows, setCustomSummaryRows, selectChooser } = useSummaryRows({
        payrollBreakdownSummaryRows: summaryRowsWithGross,
        absences: _.absences || [],
        overtimePaid100: _.overtimePaid100,
        overtimePaid125: _.overtimePaid125,
    });

    const currentContract = missionData?.getMission?.currentContract;

    // mission contains fees or deductions that need worked days?
    const needWorkDays = useMemo(() => {
        return (
            (!!currentContract?.fees &&
                currentContract.fees.filter((fee) => fee.repetition === RepetitionType.WorkedDay)
                    .length > 0) ||
            (!!currentContract?.deductions &&
                currentContract.deductions.filter(
                    (deduction) => deduction.repetition === RepetitionType.WorkedDay,
                ).length > 0)
        );
    }, [currentContract?.fees, currentContract?.deductions]);

    const { showSnackbar } = useSnackbar();

    const handleSave = useCallback(
        async (data: PayrollBreakdownData) => {
            await savePayrollBreakdown({
                variables: {
                    payrollBreakdownId: payrollBreakdownId,
                    data: _buildPayrollBreakdownInputData(data),
                },
            });
        },
        [_buildPayrollBreakdownInputData, payrollBreakdownId, savePayrollBreakdown],
    );

    // autosave when user stop changing data for 3 seconds
    const handleSaveDebounced = useDebouncedCallback(handleSave, 3000, {});

    const { setAlert } = useSetAlert();

    const handleSubmit = useCallback(async () => {
        if (!validData) {
            return;
        }

        // BAS-2779: only block when an unsigned amendment impacts the payroll
        // breakdown period. An amendment taking effect after the end of the period
        // (future period) must not block submission.
        const periodEnd = payrollBreakdown?.endDate;
        const conflictingAmendment = periodEnd
            ? getMissionAmendments.find(
                  (amendment) =>
                      amendment.state !== AmendmentState.Rejected &&
                      amendment.state !== AmendmentState.Canceled &&
                      amendment.signingState != null &&
                      PENDING_AMENDMENT_SIGNING_STATES.includes(amendment.signingState) &&
                      amendment.applyDate <= periodEnd,
              )
            : undefined;

        let alertMessage;

        switch (conflictingAmendment?.signingState) {
            case AmendmentSimpleSigningStateType.WaitingClient:
                alertMessage = i18n.t(
                    "payroll_breakdown.pending_amendment_warning.awaiting_client",
                );
                break;
            case AmendmentSimpleSigningStateType.WaitingWorker:
                alertMessage = i18n.t(
                    "payroll_breakdown.pending_amendment_warning.awaiting_worker",
                );
                break;
            case AmendmentSimpleSigningStateType.WaitingBasquiat:
                alertMessage = i18n.t(
                    "payroll_breakdown.pending_amendment_warning.awaiting_basquiat",
                );
                break;
        }

        if (alertMessage) {
            setAlert({
                title: i18n.t("payroll_breakdown.pending_amendment_warning.title"),
                message: alertMessage,
            });
            return;
        }

        handleSaveDebounced.cancel();
        void trackEvent("click_submit_payroll_breakdown");
        await submitPayrollBreakdown({
            variables: {
                payrollBreakdownId: payrollBreakdownId,
                data: _buildPayrollBreakdownInputData(validData),
            },
        });
        showSnackbar({
            text: i18n.t("screens.payroll_breakdown_editor.snackbar_submitted"),
            mode: "success",
        });
        navigation.dispatch(
            StackActions.replace("PayrollBreakdownDetails", {
                payrollBreakdownId: payrollBreakdownId,
            }),
        );
    }, [
        getMissionAmendments,
        payrollBreakdown?.endDate,
        validData,
        handleSaveDebounced,
        trackEvent,
        submitPayrollBreakdown,
        payrollBreakdownId,
        _buildPayrollBreakdownInputData,
        showSnackbar,
        i18n,
        navigation,
        setAlert,
    ]);

    const onChangeData = useCallback(
        (data: PayrollBreakdownData) => {
            setData((old) => {
                if (isEqual(old, data)) {
                    return old;
                }
                if (data) {
                    void handleSaveDebounced(data);
                }
                return data;
            });
        },
        [handleSaveDebounced],
    );

    //no timesheets are considered as completed as for monthly mission we can have the first month without any timesheet.
    const allCompleted = !!(
        payrollBreakdown?.timesheets.length === 0 ||
        payrollBreakdown?.timesheets?.every(
            (ts) => ts.status === TimeSheetState.Submitted || ts.status === TimeSheetState.Accepted,
        )
    );

    const lastTimeSheetToDate =
        (payrollBreakdown?.timesheets?.length || 0) > 0
            ? payrollBreakdown?.timesheets.reduce((latest, current) => {
                  if (!current.toDate) return latest;
                  if (!latest?.toDate) return current;
                  return new Date(current.toDate) > new Date(latest.toDate) ? current : latest;
              })?.toDate
            : undefined;

    const allTsCompleted = useMemo(() => {
        return (
            allCompleted &&
            lastTimeSheetToDate &&
            payrollBreakdown?.endDate &&
            lastTimeSheetToDate >= payrollBreakdown?.endDate
        );
    }, [allCompleted, lastTimeSheetToDate, payrollBreakdown?.endDate]);

    const canSubmit = useMemo(() => {
        return (
            (isFromTimesheet && validData && allTsCompleted) || // in timesheet we check if they are completed
            (!isFromTimesheet && validData && validData.usedVacationMinutes !== undefined)
        ); // in external we check that data are set.
    }, [isFromTimesheet, allTsCompleted, validData]);

    const showSubmitButton = useMemo(() => {
        return (
            profileType === ProfileType.Client &&
            payrollBreakdown?.status == PayrollBreakdownStatus.Open
        );
    }, [payrollBreakdown?.status, profileType]);

    const isEmptyPeriod = useMemo(
        () => !!payrollBreakdown && isEmptyPayrollBreakdownPeriod(payrollBreakdown),
        [payrollBreakdown],
    );

    const descriptionStatus = useMemo(() => {
        if (payrollBreakdown?.status === PayrollBreakdownStatus.Submitted) {
            return "submitted";
        } else {
            if (isEmptyPeriod) {
                return "empty_period";
            }
            if (!isFromTimesheet) {
                return "external_ts";
            }
            if (allTsCompleted) {
                return "lift_ts_completed";
            } else {
                return "lift_ts_open";
            }
        }
    }, [payrollBreakdown?.status, isEmptyPeriod, isFromTimesheet, allTsCompleted]);

    const { screenLabel, screenSubLabel } = usePayrollBreakdownFormatter();
    const { onScroll, headerBackgroundColor } = useScrollHeaderColorSync({
        aboveLimitColor: colors.accentLighter,
        belowLimitColor: "white",
    });

    const { presentMessageComposer } = useIntercom();

    const [buttons, optionalActions] = useMemo<[ButtonType[], ScreenCardOptionalAction[]]>(() => {
        if (showSubmitButton) {
            return [
                [
                    <SwipeButton
                        key="key"
                        size="large"
                        testID="button-submit-payroll-breakdown"
                        disabled={!canSubmit}
                        processing={processing || loading || vacationStatusLoading || saving}
                        title={i18n.t("payroll_breakdown.button_submit")}
                        onSwipeSuccess={handleSubmit}
                        thumbIconName="swipe-right"
                        mode="success"
                    />,
                ],
                [
                    ...(isFromTimesheet
                        ? [
                              {
                                  label: i18n.t(
                                      "screens.payroll_breakdown_editor.review_timesheets",
                                  ),
                                  description: i18n.t(
                                      "screens.payroll_breakdown_editor.review_timesheets_description",
                                  ),
                                  onPress: () => {
                                      if (payrollBreakdown) {
                                          navigation.navigate("PayrollBreakdownTimeSheetScreen", {
                                              payrollBreakdownId: payrollBreakdown.id,
                                          });
                                      }
                                  },
                              },
                          ]
                        : []),
                ],
            ];
        }
        return [[], []];
    }, [
        canSubmit,
        handleSubmit,
        i18n,
        isFromTimesheet,
        loading,
        navigation,
        payrollBreakdown,
        processing,
        saving,
        showSubmitButton,
        vacationStatusLoading,
    ]);

    return (
        <View style={{ flex: 1 }}>
            <ScreenHeader
                label={screenLabel(payrollBreakdown)}
                subLabel={screenSubLabel(payrollBreakdown)}
                onLeftAction={onBack}
                leftActionDisabled={loading || vacationStatusLoading}
                actions={[
                    {
                        icon: "circle-question",
                        label: i18n.t("assistance"),
                        onPress: () => {
                            void presentMessageComposer();
                        },
                    },
                ]}
                useSmallLabel
                backgroundColor={headerBackgroundColor}
            />
            <ScreenCard
                ref={screenCardRef}
                onScroll={onScroll}
                buttons={buttons}
                optionalActions={optionalActions}
                headband={
                    error || vacationStatusError
                        ? buildErrorHeadband(i18n.t("screens.payroll_breakdown_editor.cannot_load"))
                        : undefined
                }
                withAssistanceButton
            >
                {!canRenderEditor && (loading || vacationStatusLoading) && <Loading />}
                {canRenderEditor && payrollBreakdown && vacationStatus && Editor && (
                    <VStack>
                        {payrollBreakdown.isExtraMonth && (
                            <VStack h="2/3" style={{ backgroundColor: colors.accentLighter }}>
                                <Callout
                                    markdown={i18n.t("screens.payroll_breakdown.is_extra_month")}
                                    mode="warning"
                                />
                            </VStack>
                        )}
                        <PayrollBreakdownInfo
                            payrollBreakdown={payrollBreakdown}
                            introSentence={i18n.t(
                                `screens.payroll_breakdown.info_text.${profileType}.${descriptionStatus}`,
                                {
                                    workerName: payrollBreakdown.missionInfo?.workerInfo?.name,
                                    clientName:
                                        payrollBreakdown.missionInfo?.clientInfo?.profileName,
                                    companyName: payrollBreakdown.missionInfo?.clientInfo?.name,
                                    date: payrollBreakdown.submittedAt
                                        ? formatDate(
                                              new Date(payrollBreakdown.submittedAt),
                                              FULL_DATE_FORMAT,
                                          )
                                        : "",
                                },
                            )}
                        />
                        <Editor
                            payrollBreakdown={payrollBreakdown}
                            mission={missionData?.getMission || undefined}
                            vacationStatus={vacationStatus}
                            profileType={ProfileType.Client}
                            navigation={navigation}
                            onValidData={setValidData}
                            onChange={onChangeData}
                            setCustomSummaryRows={setCustomSummaryRows}
                            needWorkDays={needWorkDays}
                            isFromTimesheet={isFromTimesheet}
                            timeSheetsNeedCompletion={!allTsCompleted}
                            isEmptyPeriod={isEmptyPeriod}
                        />
                        {(allTsCompleted || !isFromTimesheet) && validData && (
                            <>
                                <PayrollBreakdownSummary
                                    rows={summaryRows}
                                    payrollBreakdownStatus={payrollBreakdown.status}
                                    needProcessing={
                                        payrollBreakdownData?.getPayrollBreakdownSummary
                                            .needProcessing
                                    }
                                    loading={payrollBreakdownLoading}
                                    error={payrollBreakdownError}
                                />
                                {selectChooser}
                            </>
                        )}
                    </VStack>
                )}
            </ScreenCard>
        </View>
    );
};

export default PayrollBreakdownEditorScreen;
